import {
  qboBillDetail,
  qboBillTitle,
} from "@/accounts-payable/quickbooks-bill";
import {
  AP_ACTION_TYPE,
  type ApAgentAction,
  type ApReceiptTarget,
} from "@/accounts-payable/types";
import { formatDollars } from "./format";
import { actionTypeMeta } from "./status-meta";

export type PricebookCostChange = {
  direction: "raise" | "lower" | "none";
  pct: number | null;
};

export const pricebookCostChange = (
  current: number,
  proposed: number,
): PricebookCostChange => {
  if (!(current > 0)) return { direction: "none", pct: null };
  if (current === proposed) return { direction: "none", pct: 0 };

  return {
    direction: proposed > current ? "raise" : "lower",
    pct: ((proposed - current) / current) * 100,
  };
};

const COST_CHANGE_VERB: Record<PricebookCostChange["direction"], string> = {
  raise: "Raise",
  lower: "Lower",
  none: "Update",
};

/** An update_pricebook_cost that leaves the cost alone and only sets `chargeable`. */
const chargeableOnly = (p: ApAgentAction["payload"]): boolean =>
  p.proposed_cost == null && p.chargeable != null;

const chargeableText = (chargeable: boolean) =>
  chargeable ? "chargeable" : "not chargeable";

export const receiptReference = (target: ApReceiptTarget): string =>
  target.receipt_number || `#${target.st_receipt_id}`;

// A return names its job only when the credit memo does; otherwise it is
// filed against the PO, or against nothing.
const returnTarget = (p: {
  st_job_external_id?: number | null;
  st_purchase_order_id?: number | null;
}): string => {
  if (p.st_job_external_id) return `on job #${p.st_job_external_id}`;
  return p.st_purchase_order_id
    ? `on purchase order #${p.st_purchase_order_id}`
    : "with no job";
};

const returnDetail = (p: {
  st_job_external_id?: number | null;
  st_purchase_order_id?: number | null;
}): string => {
  if (p.st_job_external_id) return `Job #${p.st_job_external_id}`;
  return p.st_purchase_order_id
    ? `Purchase order #${p.st_purchase_order_id}`
    : "Return with no job";
};

export const actionTitle = (a: ApAgentAction): string => {
  const p = a.payload;
  switch (a.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return p.st_project_external_id
        ? `Match to ServiceTitan project #${p.st_project_external_id}`
        : `Match to ServiceTitan job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.linkVendor:
    case AP_ACTION_TYPE.mergeVendor:
      return `Link to ST vendor "${p.st_vendor_name}"`;
    case AP_ACTION_TYPE.updatePricebookCost:
      if (chargeableOnly(p))
        return `Make ${p.sku} ${chargeableText(p.chargeable)}`;
      return `${COST_CHANGE_VERB[pricebookCostChange(p.current_cost, p.proposed_cost).direction]} cost of ${
        p.sku
      }: ${formatDollars(p.current_cost)} → ${formatDollars(p.proposed_cost)}`;
    case AP_ACTION_TYPE.createPricebookItem:
      return p.adopted_item
        ? `Use existing pricebook item ${p.adopted_item.code}`
        : `Add "${p.name}" to pricebook`;
    case AP_ACTION_TYPE.updatePo:
      return `Update purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.markNonJob:
      return "Mark as a non-job expense";
    case AP_ACTION_TYPE.createPo:
      if (p.non_job) return "Create purchase order with no job";
      return p.st_project_external_id
        ? `Create purchase order on project #${p.st_project_external_id}`
        : `Create purchase order on job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.createReturn:
      return `Create return ${returnTarget(p)}`;
    case AP_ACTION_TYPE.createReceipt:
      return `Receive purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.cancelReceipt:
      return `Cancel receipt ${receiptReference(p)}`;
    case AP_ACTION_TYPE.replaceReceipt:
      return `Replace receipt ${receiptReference(p)}`;
    case AP_ACTION_TYPE.flagForReview:
      return p.reason || "Flag for human review";
    case AP_ACTION_TYPE.noAction:
      return p.reason || "No action needed";
    case AP_ACTION_TYPE.createQboBill:
      return qboBillTitle(p);
    case AP_ACTION_TYPE.updateQboBill:
      return qboBillTitle(p, "update");
    default:
      return "Proposed action";
  }
};

export const actionCategoryLabel = (a: ApAgentAction): string => {
  if (
    a.action_type === AP_ACTION_TYPE.createPricebookItem &&
    a.payload.adopted_item
  ) {
    return "Use existing pricebook item";
  }
  if (a.action_type === AP_ACTION_TYPE.updatePricebookCost) {
    if (chargeableOnly(a.payload)) return "Update chargeable";
    const { direction } = pricebookCostChange(
      a.payload.current_cost,
      a.payload.proposed_cost,
    );
    return `${COST_CHANGE_VERB[direction]} cost`;
  }
  if (
    a.action_type === AP_ACTION_TYPE.matchJob &&
    a.payload.st_project_external_id
  )
    return "Match project";
  return actionTypeMeta(a.action_type).label;
};

export const actionDetail = (a: ApAgentAction): string => {
  const p = a.payload;
  switch (a.action_type) {
    case AP_ACTION_TYPE.matchJob:
      return p.st_project_external_id
        ? `ServiceTitan project #${p.st_project_external_id}`
        : `ServiceTitan job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.linkVendor:
    case AP_ACTION_TYPE.mergeVendor:
      return `"${p.st_vendor_name}"`;
    case AP_ACTION_TYPE.updatePricebookCost:
      if (chargeableOnly(p))
        return `${p.sku} · ${chargeableText(p.chargeable)}`;
      return `${p.sku} · ${formatDollars(p.current_cost)} → ${formatDollars(
        p.proposed_cost,
      )}`;
    case AP_ACTION_TYPE.createPricebookItem:
      return p.adopted_item
        ? `"${p.adopted_item.name ?? p.adopted_item.code}"`
        : `"${p.name}"`;
    case AP_ACTION_TYPE.updatePo:
      return `Purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.markNonJob:
      return p.reason || "Non-job expense";
    case AP_ACTION_TYPE.createPo:
      if (p.non_job) return "Purchase order with no job";
      return p.st_project_external_id
        ? `Project #${p.st_project_external_id}`
        : `Job #${p.st_job_external_id}`;
    case AP_ACTION_TYPE.createReturn:
      return returnDetail(p);
    case AP_ACTION_TYPE.createReceipt:
      return `Purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.cancelReceipt:
    case AP_ACTION_TYPE.replaceReceipt:
      return `Receipt ${receiptReference(p)} on purchase order #${p.st_purchase_order_id}`;
    case AP_ACTION_TYPE.flagForReview:
      return p.reason || "Flag for human review";
    case AP_ACTION_TYPE.noAction:
      return p.reason || "Reviewed, no change";
    case AP_ACTION_TYPE.createQboBill:
    case AP_ACTION_TYPE.updateQboBill:
      return qboBillDetail(p);
    default:
      return "Proposed action";
  }
};
